
<template>
    <v-sheet color="transparent" class="containerAreaSheet" v-click-outside="{handler: onClickOutside}" @click="activeClick">
        <Chart :class="isactive ? 'borderActive' : ''" ref="chartRef" :constructorType="'stockChart'" class="hc" :options="finalOptions"></Chart>
        <v-sheet v-if="!isactive" class="areaChartSheet" ></v-sheet>
    </v-sheet>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted }    from 'vue';

/********************************
 ********** COMPONENTS **********
 *******************************/
import {Chart} from 'highcharts-vue';

/********************************
 ************ VUETIFY ***********
 *******************************/
import { useTheme } from 'vuetify';
const theme = useTheme();

/********************************
 ************ I18N **************
 *******************************/
import { useI18n } from 'vue-i18n';
const { t } = useI18n();

/********************************
 *********** FUNCTION ***********
 *******************************/
import { getOptionsInGraphs } from '@/functions/project';

/********************************
 ********** CONSTANTES **********
 *******************************/
import CONSTGRAPHS from '@/constants/graphs';
import { STRING_GENERAL } from '@/constants/labels';

/********************************
 ********** INTERFACES **********
 *******************************/
interface Tooltip {
  formatter?: (this: { value?: number, y?:number, x?:number, color?:string }) => string;
  valueDecimals? : number;
}
 
const props = defineProps({
    firstArray:{ required:true, type:Array },
    name:{ type:String, required:true },
    title:{ required:true, type: String },
    color:{ required:true },
    isactive:{ type:Boolean, default: false }
})
        
    /**
     * Vue / Vuetify
     */


const colorText             = computed(() => theme.global.current.value.colors.colorText)
const zones                 = ref(CONSTGRAPHS.ZONES[props.name] || []);
const potLine               = CONSTGRAPHS.POTLINE[props.name];

const principalData         = computed(() => props.firstArray)

const type                  = ref(CONSTGRAPHS.TYPE_GRAPH[props.name] || null);
const reversed              = ref(props.name === STRING_GENERAL.rssi || props.name === STRING_GENERAL.rsrp || props.name === STRING_GENERAL.rsrq ? true : false)
const yAxisTitle            = ref(CONSTGRAPHS.YAXISLABEL[props.name] || '');
const yourLabels            = [ t('labels.down'), t('labels.alarm'), t('labels.alert'), t('labels.ok')];
const labels:Tooltip        = props.name === 'Status' ? 
{
    formatter: function(){
        return yourLabels[this.value??0];
    }
} : 
{};

const dataGrouping = props.name === STRING_GENERAL.Status || props.name === STRING_GENERAL.rssi || props.name === STRING_GENERAL.rsrp || props.name === STRING_GENERAL.rsrq ? {
approximation: 'average', 
enabled: true,
} : props.name === STRING_GENERAL.Voltage || props.name === STRING_GENERAL.Cost || props.name === STRING_GENERAL.temperature ? {
    approximation: 'average', 
    enabled: false,
} : {};

const tooltip:Tooltip =  props.name === STRING_GENERAL.Status ? {
    formatter: function(){

        const valor         = this.x;
        const newDate       = new Date(valor as number);
        const partsOfDate   = newDate.toString().split(' ');

        const finalDate = `${partsOfDate[1]} ${partsOfDate[2]} ${partsOfDate[3]} ${partsOfDate[4]}`; 

        var label;
        
        switch(true){
            case ((this.y??0) >= 0 && (this.y??0) < 1):
                label = t('labels.down');
                break;
            case ((this.y??0) >= 1 && (this.y??0) < 2):
                label = t('labels.alarm');
                break;
            case ((this.y??0) >= 2 && (this.y??0) < 3):
                label = t('labels.alert');
                break;
            case ((this.y??0) == 3):
                label = t('labels.ok');
                break;
        }
        return '<b> Valor: ' + label + '<br/>Fecha: ' + finalDate;
    },
    
} : props.name === 'cpu'  ? {
    formatter: function(){
        return t('labels.cpu') + `: ` + (this.y??0) + ' %' ;
    }
} : props.name === 'memoria' ? {
    formatter: function(){
        return t('labels.memo') + `: ` + (this.y??0) + ' %' ;
    }
} : props.name === 'disco' ? {
    formatter: function(){ 
        return t('labels.disc') + `: ` + (this.y??0) + ' %' ;
    }
} : props.name === 'temperature' ? {
    formatter: function(){ 
        return t('labels.temperature')+ `: <b>`  + (this.y??0) + '° C' ;
    }
} : props.name === 'voltage' ? {
    formatter: function(){ 
        return t('labels.voltage')+ `: <b>` + (this.y??0) + ' V' ;
    }
} : props.name === 'Latency' ? {
    formatter: function(){ 
        return t('labels.latency')+ `: <b>` +(this.y??0) + ' ms' ;
    }
} : { valueDecimals: 2 };


const finalYAxis = ref({
        reversed:reversed,
        title: {
            text: yAxisTitle,
            style:{ color: colorText }
        },
        plotLines: [ {
            value: potLine,
            color: 'red',
            dashStyle: 'shortdash',
            width: 2,
            label: { text:  props.name === 'Latency' ? '100ms' : potLine + ' V' }
        }],
        labels: labels,
        style: { color: colorText }
})
const finalSeries = ref([
        {
            type: type,
            name: props.name,
            data: principalData.value,
            color:props.color,
            zones:zones.value,
            dataGrouping: dataGrouping,
            linecap: 'round'
        }
])
/* eslint-disable */
/**
 * getOptionsInGraphs(tipo, serie, titulo, subtitulo, ocultar leyenda, altura, tooltip, yaxis)
 */
const finalOptions:any = ref(getOptionsInGraphs('line',finalSeries.value,props.title,'',false,550,tooltip,finalYAxis));


const isactive = ref(false);

const onClickOutside = () => {
    isactive.value = false;
}

const activeClick = () =>{
    isactive.value = true;
}


const chartRef = ref<any>();
const duplicateChart = ref();

onMounted(async () => {
    setTimeout(() => {
        duplicateChart.value = chartRef.value;
    }, 500);
});

onUnmounted(() => {
    if(duplicateChart.value){
        duplicateChart.value.chart.destroy();
    }
});
</script>

<style>
.highcharts-range-selector-group .highcharts-label text,
.highcharts-yaxis-labels text{
    color: rgb(var(--v-theme-colorText)) !important;
    fill: rgb(var(--v-theme-colorText)) !important;
}

.containerAreaSheet{
    position:relative;
    z-index: 3;
}
.areaChartSheet{
    position:absolute;
    top: 0%;
    width: 100%;
    height: 100%;
    border-radius: 20px;
    z-index: 2;
    background-color: rgba(0,0,0,0.05);
}
.areaChartSheet:hover{
    border: 2px solid rgba(255,255,255,0.5);
}

</style>